import MediaPlanLayout from "./MediaPlanLayout";
import {
    GENERATE_MEDIA_PLAN_CONFIG,
    agDefaultColDef,
    agDefaultSidebar,
    customFilterOptions,
    overlayLoadingTemplate,
} from "@/ts/constants";
import { numericValueFormatter, titleCase } from "@/ts/utils";
import { compact, filter, map } from "underscore";
import { ColDef, ExcelStyle } from "ag-grid-community";
import { useSelector } from "react-redux";
import {
    CardTitles,
    IMediaPlanRowObj,
    IValueBoolean,
    TCommonRowData,
    TMediaPlanConfig,
    TMediaPlanRowData,
} from "@/ts/common-types";
import MediaGridLayout from "@/components/wrappers/layouts/MediaGridLayout";
import { useEffect, useMemo, useState } from "react";
import { Col, Row, Spin } from "antd";
import {
    PlanMetricIndicatorCellRenderer,
    PlanRatioCellRenderer,
    PlanSpendCellRenderer,
} from "@/components/cell-renderers/AllCellRenderers";
import TargetAchievedCard from "./TargetAchievedCard";

const CreateOrEditMediaPlan = () => {
    const excelStyles = useMemo<ExcelStyle[]>(() => {
        return [
            {
                id: "header",
                alignment: {
                    vertical: "Center",
                    horizontal: "Center",
                },
                font: {
                    bold: true,
                },
                borders: {
                    borderBottom: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                    borderLeft: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                    borderRight: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                    borderTop: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                },
            },
            {
                id: "cell",
                alignment: {
                    vertical: "Center",
                    horizontal: "Center",
                },
                numberFormat: {
                    format: "#,##0",
                },
            },
            {
                id: "recordKey",
                font: {
                    italic: true,
                },
                borders: {
                    borderRight: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                    borderBottom: {
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                },
            },
            {
                id: "absoluteMetricCell",
                numberFormat: {
                    format: "#,##0",
                },
            },
            {
                id: "ratioMetricCell",
                numberFormat: {
                    format: "#,##0.00",
                },
            },
        ];
    }, []);

    const mediaPlanConfigValues = useSelector(
        (state: { mediaPlanConfigValues: { value: TMediaPlanConfig } }) =>
            state.mediaPlanConfigValues.value
    );

    const isGenerateMediaPlanLoading = useSelector(
        (state: { isGenerateMediaPlanLoading: IValueBoolean }) =>
            state.isGenerateMediaPlanLoading.value
    );

    const mediaPlanData = useSelector(
        (state: { generateMediaPlanData: { value: TMediaPlanRowData } }) =>
            state.generateMediaPlanData.value
    );

    const isMediaPlanEdit = useSelector(
        (state: { isMediaPlanEdit: IValueBoolean }) =>
            state.isMediaPlanEdit.value
    );

    const isMediaPlanCreate = useSelector(
        (state: { isMediaPlanCreate: IValueBoolean }) =>
            state.isMediaPlanCreate.value
    );

    const [rowData, setRowData] = useState<TMediaPlanRowData>({});
    const [accountLevelData, setAccountLevelData] = useState<IMediaPlanRowObj>({});

    useEffect(() => {
        const parsedRowData = JSON.parse(JSON.stringify(mediaPlanData));
        setRowData(parsedRowData);
        setAccountLevelData(parsedRowData?.Account?.[0] ?? {});
    }, [mediaPlanData]);

    const commonColDefs: ColDef[] = [
        {
            field: "impression",
            headerName: "Impressions",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_impression",
            headerName: "Achieved Impr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            hide: true,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            field: "achieved_impression_perc",
            headerName: "Achieved Impr %",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            hide: true,
            valueFormatter: numericValueFormatter,
            cellRenderer: PlanMetricIndicatorCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: true,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "Clicks",
            cellStyle: { whiteSpace: "normal" },
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_click",
            headerName: "Achieved Clicks",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            field: "achieved_click_perc",
            headerName: "Achieved Clicks %",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: true,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "Spends",
            cellStyle: { whiteSpace: "normal" },
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanSpendCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_spend",
            headerName: "Achieved Spends",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            field: "achieved_spend_perc",
            headerName: "Achieved Spends %",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: true,
                decimalPlace: 0,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "Orders",
            cellStyle: { whiteSpace: "normal" },
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_orders",
            headerName: "Achieved Orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            field: "achieved_orders_perc",
            headerName: "Achieved Orders %",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: true,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "Sales",
            cellStyle: { whiteSpace: "normal" },
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            minWidth: 160,
            maxWidth: 200,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_sale",
            headerName: "Achieved Sales",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            field: "achieved_sale_perc",
            headerName: "Achieved Sales %",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: true,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "CTR",
            cellStyle: { whiteSpace: "normal" },
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanRatioCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_ctr",
            headerName: "Achieved CTR",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            hide: true,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: PlanMetricIndicatorCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "CPC",
            cellStyle: { whiteSpace: "normal" },
            field: "cpc",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanRatioCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_cpc",
            headerName: "Achieved CPC",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            hide: true,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "CPM",
            cellStyle: { whiteSpace: "normal" },
            field: "cpm",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanRatioCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_cpm",
            headerName: "Achieved CPM",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            hide: true,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "CR",
            cellStyle: { whiteSpace: "normal" },
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanRatioCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_cr",
            headerName: "Achieved CR",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            hide: true,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            cellRenderer: PlanMetricIndicatorCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "RoAS",
            cellStyle: { whiteSpace: "normal" },
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_roas",
            headerName: "Achieved RoAS",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 110,
            maxWidth: 120,
            cellClassRules: {
                ratioMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: PlanMetricIndicatorCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
        {
            headerName: "AoV",
            cellStyle: { whiteSpace: "normal" },
            field: "aov",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            cellRenderer: PlanRatioCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                isTrackingSpecificColumn: false,
            },
        },
        {
            field: "achieved_aov",
            headerName: "Achieved AoV",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 160,
            maxWidth: 200,
            hide: true,
            cellClassRules: {
                absoluteMetricCell: () => true,
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: PlanMetricIndicatorCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                isTrackingSpecificColumn: true,
            },
        },
    ];

    const getGridHeight = (
        data: IMediaPlanRowObj[],
        rowHeight = 50,
        minHeight = 160,
        maxHeight = 500
    ) => {
        const calculatedHeight = data.length * rowHeight;
        if (data.length <= 5) {
            return calculatedHeight + 110;
        }
        return Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
    };

    return (
        <>
            {isGenerateMediaPlanLoading ? (
                <MediaPlanLayout>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100vh",
                        }}
                    >
                        <Spin
                            style={{ transform: "translateY(-30%)" }}
                            tip="Loading"
                            size="large"
                        ></Spin>
                    </div>
                </MediaPlanLayout>
            ) : (
                    <div
                        style={{
                            overflowY: "visible",
                            maxHeight: "90vh",
                        }}
                    >
                    <MediaPlanLayout>
                            {!(isMediaPlanEdit || isMediaPlanCreate) &&
                                Object.keys(accountLevelData).length ? (
                                <div className="mx-1">
                                    <Row gutter={16} justify="center">
                                        {map(
                                            Object.keys(CardTitles),
                                            (cardMetric) => {
                                                return (
                                                    <Col span={8}>
                                                        <TargetAchievedCard
                                                            metric={cardMetric}
                                                            targetAchieved={
                                                                +accountLevelData[
                                                                `achieved_${cardMetric}`
                                                            ] || 0
                                                        }
                                                        targetAchievedPerc={
                                                            +accountLevelData[
                                                                `achieved_${cardMetric}_perc`
                                                            ] || 0
                                                        }
                                                        targetSet={
                                                            +accountLevelData[
                                                                cardMetric
                                                            ] || 0
                                                        }
                                                    />
                                                </Col>
                                            );
                                            }
                                        )}
                                    </Row>
                                </div>
                            ) : null}

                            {map(
                                compact(mediaPlanConfigValues.plan_level),
                                (level) => {
                                    const mediaPlanConfig =
                                        level in GENERATE_MEDIA_PLAN_CONFIG
                                            ? GENERATE_MEDIA_PLAN_CONFIG[level]
                                            : GENERATE_MEDIA_PLAN_CONFIG["Default"];

                                const planLevelRowData = rowData[level] || [];

                                return (
                                    <div key={level} className="my-3">
                                        <div
                                            style={{
                                                backgroundColor: "#f0faff",
                                                padding: "10px 20px",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            {titleCase(level)} Level Spends
                                        </div>
                                        <div
                                            id={`${level}-grid`}
                                            className="ag-theme-alpine"
                                            style={{
                                                height: getGridHeight(
                                                    planLevelRowData
                                                ),
                                                width: "98%",
                                            }}
                                        >
                                            <MediaGridLayout
                                                {...{
                                                    rowData:
                                                        planLevelRowData as TCommonRowData,
                                                    columnDefs: [
                                                        {
                                                            field: `${mediaPlanConfig.fieldColumn}`,
                                                            headerName: `${level}`,
                                                            tooltipField: `${mediaPlanConfig.fieldColumn}`,
                                                            filter: "agTextColumnFilter",
                                                            checkboxSelection:
                                                                true,
                                                            pinned: "left",
                                                            lockPinned: true,
                                                            cellClass:
                                                                "lock-pinned",
                                                            minWidth: 400,
                                                            maxWidth: 500,
                                                            headerCheckboxSelection:
                                                                true,
                                                            cellClassRules: {
                                                                recordKey: (
                                                                    params
                                                                ) => true,
                                                            },
                                                        },
                                                        ...(isMediaPlanEdit ||
                                                            isMediaPlanCreate
                                                            ? filter(
                                                                commonColDefs,
                                                                (colDef) =>
                                                                    !colDef
                                                                        .cellRendererParams
                                                                        .isTrackingSpecificColumn
                                                            )
                                                            : commonColDefs),
                                                    ],
                                                    isRowSelectable: () => true,
                                                    animateRows: true,
                                                    defaultColDef:
                                                        agDefaultColDef,
                                                    overlayLoadingTemplate,
                                                    isTotalRowsReq: true,
                                                    sideBar: agDefaultSidebar,
                                                    planLevel: level,
                                                    excelStyles,
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </MediaPlanLayout>
                </div>
            )}
        </>
    );
};

export default CreateOrEditMediaPlan;
